import { Cart, CartDeliveryAddress } from './';
import { CalculatedCart, CalculatedCartDelivery } from './selectors';

import { Decimal } from 'decimal.js';
import {
  Currency,
  OrderAddressInput,
  OrderDataInput,
  OrderDeliveryInput,
} from './types';
import { getAdjustedDeliveryTime } from './getAdjustedDeliveryTime';

export const totalCartPrice = (cart: Cart | null) => {
  if (!cart) return { orderTotal: 0, hasZeroPricedItem: false };

  let orderTotal = new Decimal(0);
  let hasZeroPricedItem = false;

  Object.values(cart?.deliveries || {}).forEach((delivery) => {
    Object.values(delivery.items).forEach((item) => {
      const itemPrice = item.price || 0;
      orderTotal = orderTotal.add(new Decimal(item.amount || 1).mul(itemPrice));

      if (itemPrice === 0) {
        hasZeroPricedItem = true;
      }
    });
  });

  return { orderTotal: orderTotal.toNumber(), hasZeroPricedItem };
};

/**
 * Maps a cart delivery address onto the `OrderAddressInput` shape. The field
 * names are identical; this keeps the (optional) `id` reference alongside the
 * full address so the ERP can resolve the selected delivery address.
 */
export function cartDeliveryAddressToOrderAddressInput(
  address: CartDeliveryAddress,
): OrderAddressInput {
  return {
    id: address.id,
    firstName: address.firstName,
    lastName: address.lastName,
    company: address.company,
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2,
    addressLocality: address.addressLocality,
    addressDistrict: address.addressDistrict,
    addressPostcode: address.addressPostcode,
    country: address.country,
  };
}

export function cartToOrderDataInput(
  cart: CalculatedCart,

  billingAddress: OrderAddressInput,
  options?: {
    ignoreVolumeDiscounts?: boolean;
    // When enabled (useAddrMultiSelect retailers), the per-delivery address
    // selected in checkout is sent with the order. Off by default to preserve
    // existing behaviour for every other retailer (see AG-4103).
    includeDeliveryAddress?: boolean;
  },
): OrderDataInput {
  function formatCartDelivery(
    delivery: CalculatedCartDelivery,
  ): OrderDeliveryInput {
    const adjustedDeliveryTime = getAdjustedDeliveryTime(
      delivery.deliveryFrom,
      delivery.deliveryTime
    );

    const deliveryAddress =
      options?.includeDeliveryAddress && delivery.deliveryAddress
        ? cartDeliveryAddressToOrderAddressInput(delivery.deliveryAddress)
        : undefined;

    return {
      locationID: delivery.locationId.toString(),
      comments: delivery.comment,
      siloID: delivery.siloId ? delivery.siloId.toString() : undefined,
      deliveryFrom: delivery.deliveryFrom,
      deliveryTo: delivery.deliveryTo,
      deliveryTime: adjustedDeliveryTime,
      // https://agrantis.atlassian.net/browse/AG-4103
      deliveryAddress,
      items: Object.values(delivery.items)
      .sort((a, b) => a.addedAt - b.addedAt) // Sort by added time
      .map((i) => ({
        catalogueID: i.itemData.articleId.toString(),
        inventoryID: i.itemData.id.toString(),
        amount: i.itemData.quantity,
        quantity: i.amount,
        taxRate: i.itemData.taxRate,
        unit: i.itemData.unit,
        name: i.itemData.articleName,
        basePrice: i.price,
        price: options?.ignoreVolumeDiscounts
          ? i.price
          : i.discountedPrice ?? i.price,
        lineTotalNet: i.lineDiscountedTotalNet ?? i.lineTotalNet,
        lineTotalGross: i.lineDiscountedTax
          ? i.lineDiscountedTotalNet! + i.lineDiscountedTax
          : i.lineTotalGross,
        comment: i.comment,
      })),
      type: delivery.type,
    };
  }

  const payload = {
    // billingAddress, https://agrantis.atlassian.net/browse/AG-4103
    currency: Currency.Eur,
    deliveries: Object.values(cart.deliveries).map(formatCartDelivery),
  };
  return payload;
}
